// Normalizes the What-If payload (GET /cms/whatif/{processId}) into a flat,
// display-ready view model.
//
// The endpoint reports AS-IS RACI staffing: each task carries Responsible /
// Accountable / Consulted / Informational jobs with their attributed effort and
// cost. Two quirks of the payload drive the shape here:
//
//   - `accountable` (`A`) is a single object while the other three roles are
//     arrays. Everything becomes an array so the UI treats all four alike.
//   - `time_allocation_percentage` arrives as a string ("100", "0.19") and
//     `task_id` as a string while `job_id` is a number.
//
// Per-participant money is explicitly USD (`cost_usd`, and `hourly_rate` which
// reproduces it as minutes/60 * rate). Process-level cost is instead governed
// by `metrics.currency`, and process-level durations by `metrics.time_unit`, so
// both are read from the payload rather than assumed.

export const RACI_ROLES = ["R", "A", "C", "I"];

export const ROLE_LABELS = {
  R: "Responsible",
  A: "Accountable",
  C: "Consulted",
  I: "Informational",
};

// Value-added classification (FBPM Ch.6), matching the vocabulary and palette
// the Value-Added panel already uses elsewhere in the CMS.
export const VALUE_CLASSES = ["VA", "BVA", "NVA"];

// The payload reports a task's classification as `value_classification`, which
// is "unclassified" until someone has judged the task. It is modelled as its own
// value rather than an empty string so the draft can round-trip what the API
// said. The picker offers only VA/BVA/NVA, so an unclassified task simply shows
// none of the three selected.
export const UNCLASSIFIED = "UNCLASSIFIED";

// Classifying a task NVA is the modelling equivalent of eliminating it: the step
// stops happening, so its time and the labour staffed against it leave the
// process rather than merely shrinking.
//
// The backend applies this itself. A task already classified NVA in the payload
// has been deducted from `matrix.total_process_cost_usd` before it reached us,
// yet its row still arrives in full. Anything totalling those rows has to skip
// them or it will contradict the total printed beneath it — and the scenario's
// as-is baseline has to skip them too, or opening the editor would show a saving
// the backend already banked.
export const ELIMINATED_CLASS = "NVA";

export const VALUE_CLASS_META = {
  VA: {
    label: "VA",
    full: "Value-adding",
    hint: "The customer values / would pay for this.",
  },
  BVA: {
    label: "BVA",
    full: "Business value-adding",
    hint: "Necessary for the business but not customer-valued.",
  },
  NVA: {
    label: "NVA",
    full: "Non-value-adding",
    hint: "Neither — eliminating it removes its time from the process.",
  },
};

// The three components a task contributes to process time. Kept as one ordered
// list so the normalizer, the reducer and the editor cannot drift apart.
export const TIME_COMPONENTS = [
  { key: "processing", label: "Processing" },
  { key: "waiting", label: "Waiting" },
  { key: "rework", label: "Rework" },
];

export const PARTICIPANT_CURRENCY = "USD";

const MINUTES_PER_HOUR = 60;

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toText = (value) => String(value ?? "").trim();

const asArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
};

// Metrics durations follow `metrics.time_unit`; the view model standardises on
// minutes so the display layer has a single base to convert from.
const toMinutes = (value, sourceUnit) =>
  toNumber(value) * (sourceUnit === "hours" ? MINUTES_PER_HOUR : 1);

// Absent and zero are different: a job can genuinely cost nothing, so a missing
// rate has to stay null rather than collapsing to 0.
const toRate = (value) => {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeEligibleJob = (job) => {
  const jobId = toNumber(job?.job_id ?? job?.jobId, null);
  if (jobId == null) return null;

  return {
    jobId,
    jobName: toText(job?.job_name ?? job?.jobName) || `Job ${jobId}`,
    hourlyRateUsd: toRate(job?.hourly_rate ?? job?.hourlyRate),
    skillMatchPercent: toNumber(
      job?.skill_match_percentage ?? job?.skillMatchPercentage,
      null,
    ),
    isCurrent: Boolean(job?.is_current ?? job?.isCurrent),
  };
};

// A job's rate as stated on its actual assignments. These are trustworthy: they
// reproduce every `cost_usd` in the payload exactly. The per-role candidate
// lists have been observed to disagree (one job listed at 17.99 when assigned
// but 5000 as a Consulted candidate), so assignments win and the clash is
// reported rather than quietly producing absurd scenario costs.
const collectAssignedRates = (rows) => {
  const byJob = new Map();

  rows.forEach((row) => {
    RACI_ROLES.forEach((role) => {
      asArray(row?.[role]).forEach((entry) => {
        const jobId = toNumber(entry?.job_id, null);
        const rate = toRate(entry?.hourly_rate);
        if (jobId != null && rate !== null && !byJob.has(String(jobId))) {
          byJob.set(String(jobId), rate);
        }
      });
    });
  });

  return byJob;
};

// Headcount per job, indexed across the whole matrix. `role_eligible_jobs` omits
// `staff_count`, so reassigning a role to a job that is staffed elsewhere in the
// process would otherwise leave the Queuing tab quoting the *previous* holder's
// headcount. A job never staffed anywhere has none to borrow, and falls back to
// one server.
const collectAssignedStaffCounts = (rows) => {
  const byJob = new Map();

  rows.forEach((row) => {
    RACI_ROLES.forEach((role) => {
      asArray(row?.[role]).forEach((entry) => {
        const jobId = toNumber(entry?.job_id, null);
        const staff = toNumber(entry?.staff_count, null);
        if (jobId != null && staff != null && !byJob.has(String(jobId))) {
          byJob.set(String(jobId), staff);
        }
      });
    });
  });

  return byJob;
};

const resolveCandidateRate = (job, assignedRates, conflicts) => {
  const assigned = assignedRates.get(String(job.jobId));
  if (assigned === undefined) return job.hourlyRateUsd;
  if (job.hourlyRateUsd === null) return assigned;

  if (Math.abs(assigned - job.hourlyRateUsd) > 0.01) {
    conflicts.set(String(job.jobId), {
      jobName: job.jobName,
      assigned,
      listed: job.hourlyRateUsd,
    });
    return assigned;
  }

  return job.hourlyRateUsd;
};

// Current holders first, then best skill match, then alphabetical.
const compareCandidates = (a, b) => {
  if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
  const match = (b.skillMatchPercent ?? -1) - (a.skillMatchPercent ?? -1);
  if (match !== 0) return match;
  return a.jobName.localeCompare(b.jobName);
};

// Jobs each RACI role of a task may be reassigned to, supplied per role by the
// API as `role_eligible_jobs`. Incumbents are folded in so the picker always
// contains at least the current holder even if the API omits it.
const buildEligibleJobsByRole = (
  row,
  participantsByRole,
  assignedRates,
  assignedStaffCounts,
  conflicts,
) => {
  const perRole = row?.role_eligible_jobs || null;

  return RACI_ROLES.reduce((result, role) => {
    const byId = new Map();

    asArray(perRole?.[role])
      .map(normalizeEligibleJob)
      .filter(Boolean)
      .forEach((job) => {
        byId.set(String(job.jobId), {
          ...job,
          hourlyRateUsd: resolveCandidateRate(job, assignedRates, conflicts),
          staffCount: assignedStaffCounts.get(String(job.jobId)) ?? 1,
        });
      });

    participantsByRole[role].forEach((participant) => {
      if (participant.jobId == null) return;
      const key = String(participant.jobId);
      byId.set(key, {
        jobId: participant.jobId,
        jobName: participant.jobName,
        // The incumbent's own assignment is the most reliable rate available.
        hourlyRateUsd: participant.hourlyRateUsd,
        staffCount: participant.staffCount,
        skillMatchPercent: byId.get(key)?.skillMatchPercent ?? null,
        isCurrent: true,
      });
    });

    result[role] = [...byId.values()].sort(compareCandidates);
    return result;
  }, {});
};

const normalizeParticipant = (entry, role) => {
  if (!entry) return null;

  const jobId = toNumber(entry.job_id, null);
  const minutes = toNumber(entry.attributed_time_minutes);
  const hourlyRateUsd = toNumber(entry.hourly_rate);

  return {
    jobId,
    jobName: toText(entry.job_name) || `Job ${jobId ?? "?"}`,
    role,
    minutes,
    allocationPercent: toNumber(entry.time_allocation_percentage, null),
    hourlyRateUsd,
    costUsd: toNumber(
      entry.cost_usd,
      (minutes / MINUTES_PER_HOUR) * hourlyRateUsd,
    ),
    // How many people hold this job. It plays no part in cost — the rate is per
    // hour worked, not per head — but it is the server count the queueing
    // analysis needs, so the Queuing tab can open on the real staffing instead
    // of asking the user to retype what the payload already states.
    staffCount: toNumber(entry.staff_count, 1),
    source: toText(entry.source) || "original",
  };
};

// Each participant's effort is a share of one per-task figure:
//   attributed_time_minutes = baseMinutes * time_allocation_percentage / 100
// That holds exactly across the payload, so the base can be recovered and used
// to recompute effort when an allocation is edited. Attributed minutes are
// rounded to 2dp, which distorts the arithmetic badly for very small values
// (0.01 min at 0.06% back-derives to 16.67 rather than 15), so the recovery
// uses the largest contribution, where the rounding error is proportionally
// smallest.
const deriveBaseMinutes = (participants) => {
  const usable = participants.filter(
    (p) => p.allocationPercent > 0 && p.minutes > 0,
  );
  if (!usable.length) return 0;

  const strongest = usable.reduce((best, p) =>
    p.minutes > best.minutes ? p : best,
  );
  return (strongest.minutes / strongest.allocationPercent) * 100;
};

// The matrix rows are RACI-shaped, so the task's own durations are not
// guaranteed to travel with them and the key spelling varies by endpoint. Every
// plausible name is tried in order and the first numeric hit wins; an absent
// component means the task simply does not carry it, which is 0 rather than a
// gap to guess at.
const readMinutes = (row, keys) => {
  for (const key of keys) {
    const parsed = Number(row?.[key]);
    if (row?.[key] != null && row[key] !== "" && Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const TIME_KEYS = {
  processing: [
    "expected_process_time",
    "processing_time_minutes",
    "process_time_minutes",
    "task_processing_time_minutes",
  ],
  waiting: [
    "expected_waiting_time",
    "waiting_time_minutes",
    "task_waiting_time_minutes",
  ],
  rework: [
    "expected_rework_time",
    "rework_time_minutes",
    "task_rework_time_minutes",
  ],
};

// Processing time falls back to the base effort recovered from the RACI
// allocations: that figure *is* the task's own duration — every participant's
// attributed time is a percentage of it — so it is the right stand-in when the
// row omits an explicit processing time, and it keeps the task tab usable.
const normalizeTimes = (row, baseMinutes) => ({
  processing: readMinutes(row, TIME_KEYS.processing) ?? baseMinutes,
  waiting: readMinutes(row, TIME_KEYS.waiting) ?? 0,
  rework: readMinutes(row, TIME_KEYS.rework) ?? 0,
});

// Anything the API does not report as one of the three FBPM classes — including
// its own "unclassified" and a missing field — lands on UNCLASSIFIED, so the
// task tab always opens on the state the payload describes.
const normalizeClassification = (row) => {
  const raw = toText(
    row?.value_classification ?? row?.classification ?? row?.value_class,
  ).toUpperCase();
  return VALUE_CLASSES.includes(raw) ? raw : UNCLASSIFIED;
};

const normalizeTask = (row, assignedRates, assignedStaffCounts, conflicts) => {
  const participantsByRole = {};
  let participantCount = 0;
  let minutesTotal = 0;
  let summedCost = 0;

  RACI_ROLES.forEach((role) => {
    const participants = asArray(row?.[role])
      .map((entry) => normalizeParticipant(entry, role))
      .filter(Boolean);

    participantsByRole[role] = participants;
    participantCount += participants.length;
    participants.forEach((p) => {
      minutesTotal += p.minutes;
      summedCost += p.costUsd;
    });
  });

  const allParticipants = RACI_ROLES.flatMap(
    (role) => participantsByRole[role],
  );

  const baseMinutes = deriveBaseMinutes(allParticipants);
  const classification = normalizeClassification(row);

  return {
    taskId: toText(row?.task_id),
    // Names arrive with stray leading whitespace ("\t Prepare Admission plan").
    taskName: toText(row?.task_name) || "Untitled task",
    order: toNumber(row?.order, null),
    source: toText(row?.source) || "original",
    times: normalizeTimes(row, baseMinutes),
    classification,
    // Already dropped by the backend before this payload was built — see
    // ELIMINATED_CLASS. The row's own figures stay intact so the UI can show
    // what its removal saved.
    eliminated: classification === ELIMINATED_CLASS,
    participantsByRole,
    participantCount,
    minutesTotal,
    baseMinutes,
    eligibleJobsByRole: buildEligibleJobsByRole(
      row,
      participantsByRole,
      assignedRates,
      assignedStaffCounts,
      conflicts,
    ),
    totalCostUsd: toNumber(row?.task_total_cost_usd, summedCost),
  };
};

const normalizeMetrics = (metrics) => {
  const sourceTimeUnit = toText(metrics?.time_unit) || "minutes";

  return {
    sourceTimeUnit,
    currency: toText(metrics?.currency) || PARTICIPANT_CURRENCY,
    cycleTimeMinutes: toMinutes(metrics?.cycle_time_minutes, sourceTimeUnit),
    processingTimeMinutes: toMinutes(
      metrics?.processing_time_minutes,
      sourceTimeUnit,
    ),
    waitingTimeMinutes: toMinutes(metrics?.waiting_time_minutes, sourceTimeUnit),
    reworkTimeMinutes: toMinutes(metrics?.rework_time_minutes, sourceTimeUnit),
    cycleTimeEfficiencyPercent: toNumber(metrics?.cycle_time_efficiency_percent),
    taskCount: toNumber(metrics?.task_count, null),
    laborCostPerCase: toNumber(metrics?.labor_cost_per_case),
    // What the process costs per case once the NVA tasks are dropped — the
    // per-case counterpart of `to_be_total_process_cost_usd`.
    laborCostPerCaseToBe: toNumber(metrics?.labor_cost_per_case_to_be, null),
    subprocessCount: toNumber(metrics?.subprocess_count),
    // The recorded demand for the process. It is the one input the Queuing tab
    // cannot derive, so having it here saves the user retyping what the process
    // already knows — null when it has never been recorded.
    caseArrivalPerWeek: toNumber(metrics?.case_arrival_rate_per_week, null),
  };
};

const describeRateConflicts = (conflicts) =>
  [...conflicts.values()].map(
    ({ jobName, assigned, listed }) =>
      `"${jobName}" is listed as a candidate at ${listed.toFixed(2)}/hr but its assignments use ${assigned.toFixed(2)}/hr. The assignment rate is used, since it matches the reported costs.`,
  );

const collectWarnings = ({ conflicts }) => [...describeRateConflicts(conflicts)];

export function normalizeWhatIfResponse(payload) {
  const raci = payload?.raci_assignments || {};
  const matrix = raci.matrix || {};

  const rows = Array.isArray(matrix.rows) ? matrix.rows : [];

  // Rates are indexed across every row before any task is built, so a candidate
  // list can be checked against the same job's rate wherever it is assigned.
  const assignedRates = collectAssignedRates(rows);
  const assignedStaffCounts = collectAssignedStaffCounts(rows);
  const rateConflicts = new Map();

  // Task ids are not monotonic with `order` (4536 sits at order 7, 4535 at 8),
  // so sort explicitly rather than trusting array position.
  const tasks = rows
    .map((row) =>
      normalizeTask(row, assignedRates, assignedStaffCounts, rateConflicts),
    )
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const metrics = normalizeMetrics(payload?.metrics);
  const summedTaskCost = tasks.reduce((sum, t) => sum + t.totalCostUsd, 0);
  const matrixTotalUsd = toNumber(matrix.total_process_cost_usd, summedTaskCost);

  return {
    metrics,
    tasks,
    totals: {
      matrixTotalUsd,
      asIsTotalUsd: toNumber(
        matrix.as_is_total_process_cost_usd,
        matrixTotalUsd,
      ),
      toBeTotalUsd: toNumber(
        matrix.to_be_total_process_cost_usd,
        matrixTotalUsd,
      ),
      finalStaffingTotalUsd: toNumber(
        matrix.final_staffing_total_cost_usd,
        matrixTotalUsd,
      ),
      eliminatedTaskIds: (Array.isArray(matrix.eliminated_task_ids)
        ? matrix.eliminated_task_ids
        : []
      ).map(toText),
    },
    summary: {
      totalTasks: toNumber(raci.summary?.total_tasks, tasks.length),
      tasksWithAccountable: toNumber(raci.summary?.tasks_with_accountable),
      tasksWithConsulted: toNumber(raci.summary?.tasks_with_consulted),
      tasksReassigned: toNumber(raci.summary?.tasks_reassigned),
    },
    warnings: collectWarnings({ conflicts: rateConflicts }),
  };
}
